import { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock3, ExternalLink, FileUp, FolderOpen, History, LoaderCircle, Monitor, Rocket, ShieldCheck, SquareTerminal, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { MeshCommandDefinitionDto, MeshCommandJobDto, MeshDeviceDto, MeshRemoteProtocol } from '../../models';
import { createMeshCentralCommandJob, createMeshCentralRemoteSession, getApiError, getMeshCentralCommandJob, getMeshCentralCommandJobs, getMeshCentralCommands, deleteMeshCentralCommandJobs } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

type Mode = 'remote' | 'commands';

interface Props {
  device: MeshDeviceDto;
  mode: Mode;
}

const accessCards: { protocol: MeshRemoteProtocol; title: string; description: string; icon: typeof Monitor; color: string }[] = [
  { protocol: 'desktop', title: 'Рабочий стол', description: 'Экран устройства, мышь, клавиатура и полноэкранный режим.', icon: Monitor, color: 'text-cyan-700 bg-cyan-50 border-cyan-200' },
  { protocol: 'terminal', title: 'Терминал', description: 'Интерактивная командная строка именно этого устройства.', icon: SquareTerminal, color: 'text-violet-700 bg-violet-50 border-violet-200' },
  { protocol: 'files', title: 'Файлы', description: 'Просмотр каталогов, загрузка и скачивание файлов.', icon: FolderOpen, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
];

export const MeshRemoteWorkspace = ({ device, mode }: Props) => {
  const [opening, setOpening] = useState<MeshRemoteProtocol>();
  const [error, setError] = useState('');
  const [commands, setCommands] = useState<MeshCommandDefinitionDto[]>([]);
  const [jobs, setJobs] = useState<MeshCommandJobDto[]>([]);
  const [selectedCommand, setSelectedCommand] = useState<MeshCommandDefinitionDto>();
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const expectedConfirmation = device.name || device.hostname || device.nodeId;

  const loadJobs = useCallback(async () => {
    try {
      setJobs((await getMeshCentralCommandJobs(device.nodeId)).data.data);
    } catch (e) {
      setError(getApiError(e));
    }
  }, [device.nodeId]);

  const clearJobs = async () => {
    try {
      await deleteMeshCentralCommandJobs(device.nodeId);
      setJobs([]);
    } catch (e) {
      setError(getApiError(e));
    }
  };

  useEffect(() => {
    setError('');
    setSelectedCommand(undefined);
    setConfirmation('');
    if (mode !== 'commands') return;
    getMeshCentralCommands().then((response) => setCommands(response.data.data)).catch((e) => setError(getApiError(e)));
    loadJobs();
  }, [device.nodeId, loadJobs, mode]);

  const activeJobIds = useMemo(() => jobs.filter((job) => job.status === 'queued' || job.status === 'running').map((job) => job.id), [jobs]);

  useEffect(() => {
    if (mode !== 'commands' || activeJobIds.length === 0) return;
    const timer = window.setInterval(async () => {
      try {
        const updates = await Promise.all(activeJobIds.map((id) => getMeshCentralCommandJob(id)));
        setJobs((current) => current.map((job) => updates.find((item) => item.data.data.id === job.id)?.data.data || job));
      } catch (e) {
        setError(getApiError(e));
      }
    }, 4000);
    return () => window.clearInterval(timer);
  }, [activeJobIds, mode]);

  const openSession = async (protocol: MeshRemoteProtocol) => {
    const popup = window.open('', '_blank');
    if (popup) popup.opener = null;
    setOpening(protocol);
    setError('');
    try {
      const session = (await createMeshCentralRemoteSession(device.nodeId, protocol)).data.data;
      if (popup) popup.location.href = session.url;
      else window.location.href = session.url;
    } catch (e) {
      popup?.close();
      setError(getApiError(e));
    } finally {
      setOpening(undefined);
    }
  };

  const runCommand = async () => {
    if (!selectedCommand) return;
    setSubmitting(true);
    setError('');
    try {
      const job = (await createMeshCentralCommandJob(device.nodeId, selectedCommand.id, confirmation)).data.data;
      setJobs((current) => [job, ...current]);
      setSelectedCommand(undefined);
      setConfirmation('');
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === 'remote') return <div className="space-y-5">
    <div>
      <h3 className="text-lg font-semibold">Удалённый доступ к этому устройству</h3>
      <p className="mt-1 text-sm text-muted-foreground">Каждая кнопка открывает отдельную защищённую сессию MeshCentral на 15 минут.</p>
    </div>
    {error && <Alert text={error} />}
    {!device.online && <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Устройство offline. Сначала разбудите его во вкладке «Управление».</div>}
    <div className="grid gap-4 lg:grid-cols-3">
      {accessCards.map(({ protocol, title, description, icon: Icon, color }) => <article key={protocol} className="group flex min-h-56 flex-col rounded-2xl border border-border bg-card p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
        <span className={cn('flex h-12 w-12 items-center justify-center rounded-xl border', color)}><Icon className="h-6 w-6" /></span>
        <h4 className="mt-5 text-base font-semibold">{title}</h4>
        <p className="mt-2 flex-1 text-sm leading-6 text-muted-foreground">{description}</p>
        <Button className="mt-5 w-full" variant={protocol === 'desktop' ? 'default' : 'outline'} disabled={!device.online || Boolean(opening)} onClick={() => openSession(protocol)}>
          {opening === protocol ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}Открыть
        </Button>
      </article>)}
    </div>
    <div className="flex gap-3 rounded-xl border border-cyan-200 bg-cyan-50 p-4 text-cyan-950"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-cyan-700" /><div><p className="text-sm font-semibold">Доступ привязан к Node ID</p><p className="mt-1 text-xs leading-5 text-cyan-800">Ссылка короткоживущая, фиксируется в аудите и не содержит логин или пароль оператора MeshCentral.</p></div></div>
  </div>;

  return <div className="space-y-5">
    <div>
      <h3 className="text-lg font-semibold">Команды для этого устройства</h3>
      <p className="mt-1 text-sm text-muted-foreground">Только проверенные сценарии из серверного каталога. Запуск и результат сохраняются в аудите.</p>
    </div>
    {error && <Alert text={error} />}
    <div className="grid gap-4 md:grid-cols-2">
      {commands.map((command) => <article key={command.id} className="rounded-2xl border border-border p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4"><span className="flex h-11 w-11 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-700"><Rocket className="h-5 w-5" /></span>{command.destructive && <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800">Изменяет устройство</span>}</div>
        <h4 className="mt-4 font-semibold">{command.title}</h4>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{command.description}</p>
        <Button className="mt-5" disabled={!device.online || submitting} onClick={() => setSelectedCommand(command)}><FileUp className="h-4 w-4" />Подготовить запуск</Button>
      </article>)}
    </div>

    {selectedCommand && <section className="rounded-2xl border-2 border-amber-300 bg-amber-50/60 p-5">
      <h4 className="font-semibold text-amber-950">Подтверждение: {selectedCommand.title}</h4>
      <p className="mt-2 text-sm leading-6 text-amber-900">Будут скачаны файлы релиза, создан backup и перезапущен Medscreener. Введите имя устройства: <strong>{expectedConfirmation}</strong></p>
      <Input className="mt-4 max-w-xl bg-white" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder={expectedConfirmation} autoFocus />
      <div className="mt-4 flex flex-wrap gap-2"><Button variant="destructive" disabled={confirmation !== expectedConfirmation || submitting} onClick={runCommand}>{submitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}Запустить обновление</Button><Button variant="outline" onClick={() => { setSelectedCommand(undefined); setConfirmation(''); }}>Отмена</Button></div>
    </section>}

    <section>
      <div className="flex items-center justify-between gap-3"><div className="flex items-center gap-2"><History className="h-5 w-5 text-muted-foreground" /><h4 className="font-semibold">История команд</h4></div><div className="flex gap-2"><Button size="sm" variant="outline" onClick={loadJobs}>Обновить</Button>{jobs.length > 0 && <Button size="sm" variant="ghost" onClick={clearJobs} className="text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" />Очистить</Button>}</div></div>
      {jobs.length === 0 ? <div className="mt-3 rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Команды на этом устройстве ещё не запускались.</div> : <div className="mt-3 space-y-3">{jobs.map((job) => <JobCard key={job.id} job={job} />)}</div>}
    </section>
  </div>;
};

const JobCard = ({ job }: { job: MeshCommandJobDto }) => {
  const running = job.status === 'queued' || job.status === 'running';
  const status = job.status === 'completed' ? 'Выполнено' : job.status === 'failed' ? 'Ошибка' : job.status === 'running' ? 'Выполняется' : 'В очереди';
  return <article className="overflow-hidden rounded-xl border border-border">
    <div className="flex flex-wrap items-start justify-between gap-3 p-4"><div><p className="text-sm font-semibold">{job.commandTitle}</p><p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><Clock3 className="h-3.5 w-3.5" />{formatDate(job.requestedAt)} · {job.requestedBy}</p></div><span className={cn('rounded-full px-2.5 py-1 text-xs font-semibold', job.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : job.status === 'failed' ? 'bg-rose-100 text-rose-800' : 'bg-blue-100 text-blue-800')}>{running && <LoaderCircle className="mr-1 inline h-3 w-3 animate-spin" />}{status}</span></div>
    {(job.output || job.errorMessage) && <pre className="max-h-72 overflow-auto border-t border-border bg-slate-950 p-4 text-[11px] leading-5 text-slate-200">{job.output}{job.errorMessage && `\nERROR: ${job.errorMessage}`}</pre>}
  </article>;
};

const Alert = ({ text }: { text: string }) => <div role="alert" className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{text}</div>;
const formatDate = (value: string) => new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
